/**
 * Centralized API Client
 *
 * A typed, consistent API client for making HTTP requests.
 * Handles authentication, error handling, and response parsing.
 */

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || '';
const DEFAULT_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 2;
const RETRY_DELAY = 1000; // 1 second

interface RequestOptions extends Omit<RequestInit, 'body'> {
  params?: Record<string, string | undefined>;
  body?: unknown;
  signal?: AbortSignal;
  timeout?: number;
  retries?: number;
}

/**
 * Custom error class for API errors with status codes and details
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public detail?: unknown,
    public isNetworkError: boolean = false,
    public isTimeout: boolean = false
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * Check if error is retryable (network issues, 5xx errors, etc.)
   */
  get isRetryable(): boolean {
    return this.isNetworkError || this.isTimeout || (this.status >= 500 && this.status < 600);
  }

  /**
   * Check if error is an authentication error
   */
  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /**
   * Check if error is a validation error
   */
  get isValidationError(): boolean {
    return this.status === 400 || this.status === 422;
  }

  /**
   * Check if resource was not found
   */
  get isNotFound(): boolean {
    return this.status === 404;
  }
}

/**
 * Network error (no response received)
 */
export class NetworkError extends ApiError {
  constructor(message: string = 'Network error - please check your connection') {
    super(message, 0, undefined, true, false);
    this.name = 'NetworkError';
  }
}

/**
 * Timeout error
 */
export class TimeoutError extends ApiError {
  constructor(message: string = 'Request timed out - please try again') {
    super(message, 0, undefined, false, true);
    this.name = 'TimeoutError';
  }
}

/**
 * Sleep utility for retry delays
 */
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE) {
    this.baseUrl = baseUrl;
  }

  /**
   * Build URL with query parameters
   */
  private buildUrl(endpoint: string, params?: Record<string, string | undefined>): string {
    let url = `${this.baseUrl}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value);
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    return url;
  }

  /**
   * Execute a single request attempt
   */
  private async executeRequest<T>(
    url: string,
    fetchOptions: RequestInit,
    timeout: number
  ): Promise<T> {
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        signal: fetchOptions.signal || controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle HTTP errors
      if (!response.ok) {
        let errorDetail: unknown;
        try {
          errorDetail = await response.json();
        } catch {
          try {
            errorDetail = await response.text();
          } catch {
            errorDetail = undefined;
          }
        }

        const message = this.extractErrorMessage(errorDetail, response);
        throw new ApiError(message, response.status, errorDetail);
      }

      // Parse response
      if (response.status === 204) {
        return undefined as T;
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle abort/timeout
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new TimeoutError();
      }

      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new NetworkError();
      }

      // Re-throw ApiError as-is
      if (error instanceof ApiError) {
        throw error;
      }

      // Wrap unknown errors
      throw new NetworkError(
        error instanceof Error ? error.message : 'An unexpected error occurred'
      );
    }
  }

  /**
   * Extract a user-friendly error message from the response
   */
  private extractErrorMessage(errorDetail: unknown, response: Response): string {
    if (typeof errorDetail === 'object' && errorDetail !== null) {
      // FastAPI style: { detail: "message" }
      if ('detail' in errorDetail) {
        const detail = (errorDetail as { detail: unknown }).detail;
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail) && detail.length > 0) {
          // Validation errors: [{ msg: "...", loc: [...] }]
          return detail.map((d: { msg?: string }) => d.msg || 'Validation error').join(', ');
        }
      }
      // Django style: { error: "message" }
      if ('error' in errorDetail && typeof (errorDetail as { error: unknown }).error === 'string') {
        return (errorDetail as { error: string }).error;
      }
      // Generic: { message: "..." }
      if ('message' in errorDetail && typeof (errorDetail as { message: unknown }).message === 'string') {
        return (errorDetail as { message: string }).message;
      }
    }

    if (typeof errorDetail === 'string' && errorDetail.length > 0) {
      return errorDetail;
    }

    // Default message based on status code
    return this.getStatusMessage(response.status, response.statusText);
  }

  /**
   * Get human-readable message for HTTP status codes
   */
  private getStatusMessage(status: number, statusText: string): string {
    const messages: Record<number, string> = {
      400: 'Invalid request - please check your input',
      401: 'Authentication required - please log in',
      403: 'You do not have permission to perform this action',
      404: 'The requested resource was not found',
      409: 'Conflict - the resource may have been modified',
      422: 'Validation error - please check your input',
      429: 'Too many requests - please wait and try again',
      500: 'Server error - please try again later',
      502: 'Service temporarily unavailable - please try again',
      503: 'Service temporarily unavailable - please try again',
      504: 'Request timed out - please try again',
    };
    return messages[status] || `Request failed: ${status} ${statusText}`;
  }

  /**
   * Main request method with retry support
   */
  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const {
      params,
      body,
      timeout = DEFAULT_TIMEOUT,
      retries = MAX_RETRIES,
      ...fetchOptions
    } = options;

    const url = this.buildUrl(endpoint, params);

    // Prepare headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    };

    const requestInit: RequestInit = {
      credentials: 'include',
      ...fetchOptions,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    };

    // Attempt request with retries for transient failures
    let lastError: ApiError | undefined;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await this.executeRequest<T>(url, requestInit, timeout);
      } catch (error) {
        if (error instanceof ApiError) {
          lastError = error;

          // Don't retry non-retryable errors
          if (!error.isRetryable || attempt === retries) {
            throw error;
          }

          // Log retry attempt
          console.warn(
            `[API] Request failed (attempt ${attempt + 1}/${retries + 1}), retrying in ${RETRY_DELAY}ms:`,
            error.message
          );

          // Wait before retry (with exponential backoff)
          await sleep(RETRY_DELAY * Math.pow(2, attempt));
        } else {
          throw error;
        }
      }
    }

    // Should not reach here, but just in case
    throw lastError || new NetworkError('Request failed after retries');
  }

  /**
   * GET request
   */
  get<T>(endpoint: string, options?: Omit<RequestOptions, 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data,
    });
  }

  /**
   * PUT request
   */
  put<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data,
    });
  }

  /**
   * PATCH request
   */
  patch<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data,
    });
  }

  /**
   * DELETE request
   */
  delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

// Default client instance
export const apiClient = new ApiClient();

// Export for custom configurations
export { ApiClient };
